import { Button } from '@consigli/facade';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailContentOption, EmailOptionsModal } from './email-options-modal';
import { useEmailMeetingSummary } from '../hooks/use-email-meeting-summary';

import type { MeetingSummaryData } from '../types';
import type { MeetingParticipant } from '../utils/export-types';

interface EmailSummaryButtonProps {
  meeting: {
    id: string;
    title: string;
    participants?: MeetingParticipant[];
  };
  meetingData: MeetingSummaryData | null;
  projectId: number | string;
}

export const EmailSummaryButton: React.FC<EmailSummaryButtonProps> = ({
  meeting,
  meetingData,
  projectId,
}) => {
  const { t } = useTranslation();
  const { handleEmailSummary, isSendingEmail, cancelEmail } = useEmailMeetingSummary(
    meeting,
    projectId,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isDataLoading = !meetingData?.isDataReady;

  const handleConfirm = async (option: EmailContentOption, recipients: string[]) => {
    // Send email via backend
    const succeeded = await handleEmailSummary(option, recipients);
    // Only close the modal on success; preserve recipients if there was an error
    if (succeeded) {
      setIsModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    // Cancel any ongoing email send and close modal
    if (isSendingEmail) {
      cancelEmail();
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        secondary
        rounded
        onClick={() => setIsModalOpen(true)}
        disabled={isSendingEmail || isDataLoading}
      >
        {t('meeting-secretary.meetings.email-summary')}
      </Button>
      <EmailOptionsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        isLoading={isSendingEmail}
        attendees={meeting.participants}
      />
    </>
  );
};
